import { useCallback, useRef } from 'react';
import { type CalendarDate, isSameDay, isSameMonth, today } from '@internationalized/date';
import type { CalendarState, RangeCalendarState } from '@react-stately/calendar';
import { clsx } from 'clsx';
import { useCalendarCell } from 'react-aria';
import { match, P } from 'ts-pattern';
import { useIntersectionObserverAction } from '../../../../../../util/intersection-observer';
import styles from './calendar-grid-cell.module.scss';

interface CalendarGridCellProps {
  currentMonth: CalendarDate;
  date: CalendarDate;
  onVisible?: () => void;
  state: CalendarState | RangeCalendarState;
  timeZone: string;
}

export function CalendarGridCell({ currentMonth, date, onVisible, state, timeZone }: CalendarGridCellProps) {
  const [setObservedItemRef] = useIntersectionObserverAction(onVisible, {
    disabled: !onVisible,
  });
  const cellButtonRef = useRef<HTMLButtonElement>(null);
  const { cellProps, buttonProps, isSelected, formattedDate, isOutsideVisibleRange, isInvalid, isUnavailable } = useCalendarCell(
    { date },
    state,
    cellButtonRef,
  );
  const { 'aria-disabled': cellAriaDisabled, ...restOfCellProps } = cellProps;
  const { 'aria-disabled': buttonAriaDisabled, onClick: defaultOnClickHandler, ...restOfButtonProps } = buttonProps;

  const isOutsideMonth = !isSameMonth(currentMonth, date);
  const isToday = isSameDay(today(timeZone), date);
  const isInSelectionRange = match(state)
    .with({ highlightedRange: P.not(P.nullish) }, (r) => {
      const localTimezone = timeZone;

      return (
        date.toDate(localTimezone) >= r.highlightedRange.start.toDate(localTimezone) &&
        date.toDate(localTimezone) <= r.highlightedRange.end.toDate(localTimezone)
      );
    })
    .otherwise(() => isSelected);
  const isSelectionStart = match(state)
    .with({ highlightedRange: P.not(P.nullish) }, (r) => isSameDay(date, r.highlightedRange.start))
    .otherwise(() => isSelected);
  const isSelectionEnd = match(state)
    .with({ highlightedRange: P.not(P.nullish) }, (r) => isSameDay(date, r.highlightedRange.end))
    .otherwise(() => isSelected);

  const stateClassNames = match({
    isSelectionStart,
    isSelectionEnd,
    isToday,
    isInSelectionRange,
    isOutsideMonth,
    isDisabled: isOutsideVisibleRange ? isInvalid || isUnavailable : state.isDisabled || cellAriaDisabled,
  })
    .with({ isDisabled: true }, () => styles.isDisabled)
    .with({ isSelectionStart: true, isSelectionEnd: true }, () => styles.isSelectedSingleDate)
    .with({ isSelectionStart: true }, () => styles.isRangeStart)
    .with({ isSelectionEnd: true }, () => styles.isRangeEnd)
    .with({ isInSelectionRange: true }, () => styles.isWithinRange)
    .with({ isToday: true }, () => styles.isToday)
    .with({ isOutsideMonth: true }, () => styles.isOutsideMonth)
    .otherwise(() => undefined);

  const onClick = useCallback(
    (e: Parameters<NonNullable<typeof defaultOnClickHandler>>[0]) => {
      if (isOutsideVisibleRange && !isInvalid && !isUnavailable) {
        state.setFocusedDate(date);
        state.selectDate(date);
      } else {
        defaultOnClickHandler?.(e);
      }
    },

    [defaultOnClickHandler, state.setFocusedDate, state.selectDate, date, isInvalid, isUnavailable, isOutsideVisibleRange],
  );

  return (
    <td
      aria-disabled={isOutsideVisibleRange ? isInvalid || isUnavailable : cellAriaDisabled}
      className={clsx(styles.calendarGridCell, stateClassNames)}
      ref={setObservedItemRef}
      {...restOfCellProps}
    >
      <button
        aria-disabled={isOutsideVisibleRange ? isInvalid || isUnavailable : buttonAriaDisabled}
        className={styles.content}
        {...restOfButtonProps}
        onClick={onClick}
        ref={cellButtonRef}
        type="button"
      >
        {formattedDate}
      </button>
    </td>
  );
}
